import { loadPage, observeOnce } from "./utils"
import { ServicesFilter } from "./services_filter"
import VanillaFittty from "shared/scripts/vanilla_fittty"

export type AsyncLoadHooks = {
  ensureServices: () => Promise<void>
  ensureDiscoveryItems: () => Promise<void>
}

export const AsyncLoad = (): AsyncLoadHooks => {
  const { allServicesPath, belowFoldPath, currentView, discoverSsr, loadingImagePath } =
    document.querySelector(".explore-discover-container").dataset

  const allContainer = document.querySelector(".explore-discover-results")
  const serviceContainer = document.querySelector("div.web-services-container")
  const discoverItemsContent = allContainer?.querySelector(".explore-discover-tiles")

  // Tiles start hidden behind a tab on these views, and their first page is not
  // server-rendered either, so nothing loads until the visitor asks for it.
  const tilesBehindTab = currentView === "discover_services" || currentView === "search"
  const belowFoldServerRendered = discoverSsr === "complete"

  const showTilesFromActiveTab = () => {
    const activeTab = document.querySelector(".filter-content-list > a.active").dataset.target

    const discoverTab = activeTab === "all"
    const appletTab = activeTab === "applets"
    const storyTab = activeTab === "stories"

    if (!activeTab || discoverTab) {
      allContainer?.querySelectorAll("div.explore-tile.hide").forEach(tile => tile.classList.remove("hide"))
    } else if (appletTab || storyTab) {
      allContainer?.querySelectorAll("div.explore-tile.hide").forEach(tile => {
        if (tile.dataset.type == activeTab) {
          tile.classList.remove("hide")
        }
      })
    }
  }

  /**
   * `load_more` returns the whole page, including any batches already rendered
   * server-side, and the two can drift apart (the HTML is CDN-cached for 12h
   * while load_more is cached for 1h). So append by id rather than slicing at a
   * fixed offset. Tiles without an id are the "create your own" CTAs; they ride
   * along with the batch they were rendered in.
   */
  const appendNewTiles = (html: string) => {
    const parsed = document.createElement("div")
    parsed.innerHTML = html

    // Keyed by type as well as id: applet ids, service module names and story
    // slugs share one namespace here, and a collision across types would
    // silently drop a tile.
    const keyOf = (el: HTMLElement) => `${el.dataset.type}:${el.dataset.id}`

    const seen = new Set(
      Array.from(discoverItemsContent.querySelectorAll<HTMLElement>("[data-id]")).map(keyOf)
    )

    let appendedNewTile = false

    Array.from(parsed.children).forEach(node => {
      const { id } = (node as HTMLElement).dataset

      if (id === undefined) {
        if (appendedNewTile) discoverItemsContent.appendChild(node)
        return
      }

      const key = keyOf(node as HTMLElement)
      if (seen.has(key)) return

      seen.add(key)
      appendedNewTile = true
      discoverItemsContent.appendChild(node)
    })
  }

  // Rejects once its retries are spent, so the caller can un-latch its guard.
  const loadDiscoveryItemsAsync = ({ page, retries, runFittty }) =>
    loadPage(`${belowFoldPath}?page=${page}`).then(html => {
      appendNewTiles(html)
      showTilesFromActiveTab()
      // Returned, not discarded, so awaiting the loader also waits for the
      // appended tiles to be fitted and for the VanillaFittty event to fire.
      if (runFittty) return VanillaFittty(allContainer as HTMLElement)
    }).catch(error => {
      if (retries > 0) {
        return new Promise(resolve => setTimeout(resolve, 250))
          .then(() => loadDiscoveryItemsAsync({ page, retries: (retries - 1), runFittty }))
      }

      console.error(error)
      throw error
    })

  const loadingPlaceholder =
    `<div class="explore-search-loading-placeholder"><img class="loading" src="${loadingImagePath}" width="150" height="343" alt="" /></div>`

  const loadServicesAsync = () => {
    const previousMarkup = serviceContainer.innerHTML
    serviceContainer.innerHTML = loadingPlaceholder

    return loadPage(allServicesPath).then(html => {
      serviceContainer.innerHTML = html
      ServicesFilter()
    }).catch(error => {
      // Put the tab back rather than leaving the visitor on a spinner forever.
      serviceContainer.innerHTML = previousMarkup
      console.error(error)
      throw error
    })
  }

  // These guards dedupe concurrent callers (sentinel, tab click, hover), so they
  // have to be released when a request fails terminally -- otherwise one flaky
  // response permanently disables the tab.
  let servicesRequested = false
  const ensureServices = () => {
    if (servicesRequested) return Promise.resolve()

    servicesRequested = true
    return loadServicesAsync().catch(() => { servicesRequested = false })
  }

  let discoveryItemsRequested = false
  const ensureDiscoveryItems = () => {
    if (discoveryItemsRequested) return Promise.resolve()

    discoveryItemsRequested = true

    // On the tab-gated views the grid starts empty, so show something while the
    // request is in flight rather than a blank panel.
    if (!discoverItemsContent.children.length) discoverItemsContent.innerHTML = loadingPlaceholder

    const pending = []
    if (tilesBehindTab) pending.push(loadDiscoveryItemsAsync({ page: "first", retries: 1 }))
    // TabManagement drives Fittty on the views where tiles start hidden.
    pending.push(loadDiscoveryItemsAsync({ page: "rest_expanded", retries: 1, runFittty: !tilesBehindTab }))

    return Promise.allSettled(pending).then(results => {
      discoverItemsContent.querySelector(".explore-search-loading-placeholder")?.remove()
      if (results.some(result => result.status === "rejected")) discoveryItemsRequested = false
    })
  }

  if (currentView === "discover_services") {
    // Already server-rendered in full on this view.
    servicesRequested = true
    ServicesFilter()
  }

  // On the views where tiles start hidden behind a tab, nothing loads until the
  // visitor switches tabs or clears their search.
  if (belowFoldServerRendered) {
    // Only the tail of the grid is missing, and that waits for a scroll. Fittty
    // still has to run: index.ts keeps the first-page tiles invisible until the
    // VanillaFittty event fires, and no initial request dispatches it now.
    void VanillaFittty(allContainer as HTMLElement)

    const sentinel = allContainer?.querySelector(".explore-load-more-sentinel")
    if (sentinel) observeOnce(sentinel, ensureDiscoveryItems, { rootMargin: "100% 0px" })
  } else if (!tilesBehindTab) {
    ensureDiscoveryItems()
  }

  // Warm the fetch on intent so tabs still switch instantly. Both endpoints are
  // CDN-cached, so a hover is usually enough to have the content already in hand.
  document.querySelector(".filter-content-list")?.addEventListener("pointerenter", event => {
    const tab = (event.target as Element)?.closest?.("a")
    if (!tab) return

    if (tab.dataset.target === "services") ensureServices()
    else ensureDiscoveryItems()
  }, true)

  return { ensureServices, ensureDiscoveryItems }
}
